import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_DURATION_UNITS, ALL_PRICING_UNITS } from '@lims/shared-types';
import { catalogueApi, organisationApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { useSampleTypes } from '../samples/sample-type-fields.js';
import {
  ActionButton,
  Alert,
  BilingualTextField,
  ErrorState,
  FormPage,
  FormSection,
  LoadingState,
  NumberField,
  ReadOnlyField,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  useSaveErrors,
  useSaveMutation,
  useToast,
  useLoadOnce,
} from '../../components/ui/index.js';
import { formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { ReportTemplatePanel } from './ReportTemplatePanel.js';
import { SpecialisedTemplatePanel } from './SpecialisedTemplatePanel.js';
import { fromDraft, optionalText, toDraft } from '../clients/bilingual.js';
import { RecordStrip, StripItem } from '../reports/register-panel.js';

/**
 * The test card — view and edit.
 *
 * ===========================================================================
 * THE READINESS BANNER IS THE MOST IMPORTANT THING ON THIS PAGE.
 * ===========================================================================
 *
 * A test with no specialised template says so, prominently, in the laboratory's
 * own language, and says plainly what is and is not implemented. There is no
 * tab of greyed-out scientific fields behind it, because there are no
 * scientific fields: this product has never been told how any of these tests is
 * performed, and inventing a plausible-looking form would be worse than
 * admitting that (CLAUDE.md section 4).
 *
 * THE PRICE IS THE SECOND. It renders "not priced" when unset — never `0.00`.
 * A routine change of the base price needs no reason (D189); a reason may be
 * given and is kept with the audit entry, which records who changed what.
 */
export function CatalogueItemPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const isNew = id === undefined;
  const canEdit = can(PERMISSIONS.CATALOGUE_MANAGE);
  const canPrice = can(PERMISSIONS.PRICING_OVERRIDE);

  const [code, setCode] = useState('');
  const [name, setName] = useState(toDraft(null));
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [referenceStandard, setReferenceStandard] = useState('');
  const [standardVersion, setStandardVersion] = useState('');
  const [sampleType, setSampleType] = useState<string | null>(null);
  const [durationValue, setDurationValue] = useState<number | null>(null);
  const [durationUnit, setDurationUnit] = useState<string | null>(null);
  const [basePrice, setBasePrice] = useState<number | null>(null);
  const [pricingUnit, setPricingUnit] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const item = useQuery({
    queryKey: ['catalogue', id],
    queryFn: () => catalogueApi.get(id as string),
    enabled: !isNew,
  });

  const categories = useQuery({
    queryKey: ['catalogue', 'categories'],
    queryFn: () => catalogueApi.listCategories(false),
  });

  // The laboratory's own register, from Phase 6. Readable by any signed-in
  // member, which is why this form can offer it without a manage permission.
  const departments = useQuery({
    queryKey: ['departments', 'active'],
    queryFn: () => organisationApi.listDepartments(false),
  });

  // The laboratory's own sample types (D306): every active one, and the
  // entry's own type even if it has since been archived — shown as it is,
  // never swapped for another. A new choice of an archived type is refused
  // by the server too.
  const sampleTypes = useSampleTypes();
  const storedSampleType = item.data?.sampleType ?? null;
  const sampleTypeOptions = sampleTypes.types
    .filter((type) => !type.archived || type.code === storedSampleType)
    .map((type) => ({
      value: type.code,
      label: type.archived
        ? t('catalogue.sampleTypeArchived', { name: sampleTypes.nameOf(type.code) })
        : sampleTypes.nameOf(type.code),
    }));

  useLoadOnce(item.data, (record) => {
    setCode(record.code ?? '');
    setName(toDraft(record.name));
    setCategoryId(record.category?.id ?? null);
    setDepartmentId(record.department?.id ?? null);
    setReferenceStandard(record.referenceStandard ?? '');
    setStandardVersion(record.standardVersion ?? '');
    setSampleType(record.sampleType);
    setDurationValue(record.expectedDuration?.value ?? null);
    setDurationUnit(record.expectedDuration?.unit ?? null);
    // The MAJOR unit for a person to read and type. Converted back to minor
    // units on save, so no floating-point amount is ever stored.
    setBasePrice(
      record.basePriceMinor === null ? null : record.basePriceMinor / 100,
    );
    setPricingUnit(record.pricingUnit);
    setNotes(record.notes ?? '');
  });

  /**
   * Has the price actually changed? Only then is it sent: the server records
   * every change as an audit event of its own (who, when, before, after).
   * NO REASON is asked for (D189) — revising a price list is the ordinary
   * work of the person entitled to do it.
   */
  const storedPriceMajor =
    item.data?.basePriceMinor === null || item.data?.basePriceMinor === undefined
      ? null
      : item.data.basePriceMinor / 100;
  const priceChanged = isNew
    ? basePrice !== null
    : basePrice !== storedPriceMajor;

  const body = () => ({
    code: optionalText(code),
    name: fromDraft(name),
    categoryId,
    departmentId,
    referenceStandard: optionalText(referenceStandard),
    standardVersion: optionalText(standardVersion),
    sampleType,
    expectedDurationValue: durationValue,
    expectedDurationUnit: durationValue === null ? null : durationUnit,
    // Null stays null all the way to the API. An empty price field is an
    // unpriced service, not a free one.
    ...(priceChanged
      ? {
          basePriceMinor:
            basePrice === null ? null : Math.round(basePrice * 100),
        }
      : {}),
    ...(pricingUnit ? { pricingUnit } : {}),
    notes: optionalText(notes),
  });

  // Saved -> back to the catalogue list, which loads the new value (the
  // entry has no read-only page of its own; this form is its page).
  const save = useSaveMutation({
    mutationFn: () =>
      isNew
        ? catalogueApi.create(body())
        : catalogueApi.update(id as string, body()),
    successMessage: t('catalogue.saved'),
    invalidate: [['catalogue']],
    navigateTo: () => '/catalogue',
  });
  const { fields: fieldErrors } = useSaveErrors(save.error);
  const errorFor = (field: string) =>
    fieldErrors[field] ? { error: fieldErrors[field] } : {};

  const setActive = useMutation({
    mutationFn: (isActive: boolean) =>
      catalogueApi.update(id as string, {
        isActive,
        reason: isActive
          ? t('catalogue.reactivate')
          : t('catalogue.deactivate'),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      show({ tone: 'success', message: t('catalogue.saved') });
    },
  });

  if (!isNew && item.isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }
  if (!isNew && item.error) {
    return <ErrorState error={item.error} onRetry={() => void item.refetch()} />;
  }

  const record = item.data;

  const title = isNew ? t('catalogue.createTitle') : t('catalogue.editTitle');

  return (
    <>
    <FormPage
      title={title}
      breadcrumbs={[{ label: t('nav.catalogue'), to: '/catalogue' }, { label: title }]}
      actions={
        !isNew && canEdit && record ? (
          <ActionButton
            priority="secondary"
            onPress={() => setActive.mutate(!record.isActive)}
            isLoading={setActive.isPending}
          >
            {record.isActive ? t('catalogue.deactivate') : t('catalogue.reactivate')}
          </ActionButton>
        ) : null
      }
      onSubmit={() => save.mutate()}
      onCancel={() => navigate('/catalogue')}
      isSubmitting={save.isPending}
      canSubmit={canEdit}
      error={save.error}
      fieldLabels={{
        code: t('catalogue.code'),
        basePriceMinor: t('catalogue.basePrice'),
        templateReadiness: t('catalogue.readiness'),
      }}
      notices={
        record ? (
          <>
            {/*
              The record bar (D278): the entry's code, whether it is in use,
              and — first among them — whether a specialised template is ready.
            */}
            <RecordStrip>
              {record.code ? (
                <StripItem label={t('catalogue.code')}>
                  <span className="measure font-semibold" dir="ltr">
                    {record.code}
                  </span>
                </StripItem>
              ) : null}
              <StripItem label={t('common.status')}>
                <StatusBadge
                  label={record.isActive ? t('common.active') : t('catalogue.deactivated')}
                  tone={record.isActive ? 'success' : 'neutral'}
                />
              </StripItem>
              {record.referenceStandard ? (
                <StripItem label={t('catalogue.referenceStandard')}>
                  <span className="measure" dir="ltr">
                    {record.standardVersion
                      ? `${record.referenceStandard} - ${record.standardVersion}`
                      : record.referenceStandard}
                  </span>
                </StripItem>
              ) : null}
            </RecordStrip>
            {/*
              THE READINESS BANNER. Shown for every entry that has no certified
              specialised template. It states what exists and what does not,
              rather than leaving a person to assume the system will calculate
              something it cannot.
            */}
            {record.usesGeneralWorkflowOnly ? (
              <Alert
                tone="warning"
                title={t('catalogue.notReadyTitle')}
                description={t('catalogue.notReadyBody')}
              />
            ) : null}
          </>
        ) : null
      }
    >
        <FormSection title={t('catalogue.identity')}>
          <BilingualTextField
            labelAr={t('catalogue.nameAr')}
            labelEn={t('catalogue.nameEn')}
            valueAr={name.ar}
            valueEn={name.en}
            onChangeAr={(value) => setName((c) => ({ ...c, ar: value }))}
            onChangeEn={(value) => setName((c) => ({ ...c, en: value }))}
            isRequired
            isReadOnly={!canEdit}
          />
          <TextField
            label={t('catalogue.code')}
            value={code}
            onChange={setCode}
            isReadOnly={!canEdit}
            {...errorFor('code')}
          />
          <SelectField
            label={t('catalogue.categoryLabel')}
            value={categoryId}
            onChange={setCategoryId}
            placeholder={t('common.none')}
            isDisabled={!canEdit}
            options={(categories.data ?? []).map((category) => ({
              value: category.id,
              label:
                (locale === 'ar' ? category.name.ar : category.name.en) ??
                category.code ??
                '',
            }))}
          />
          <SelectField
            label={t('catalogue.departmentLabel')}
            description={t('catalogue.departmentHint')}
            value={departmentId}
            onChange={setDepartmentId}
            placeholder={t('common.none')}
            isDisabled={!canEdit}
            options={(departments.data?.data ?? []).map((department) => ({
              value: department.id,
              label: department.name || department.code || '',
            }))}
          />
        </FormSection>

        <FormSection title={t('catalogue.classification')}>
          <TextField
            label={t('catalogue.referenceStandard')}
            description={t('catalogue.referenceStandardHint')}
            value={referenceStandard}
            onChange={setReferenceStandard}
            isReadOnly={!canEdit}
          />
          <TextField
            label={t('catalogue.standardVersion')}
            value={standardVersion}
            onChange={setStandardVersion}
            isReadOnly={!canEdit}
          />
          <SelectField
            label={t('catalogue.sampleTypeLabel')}
            value={sampleType}
            onChange={setSampleType}
            placeholder={t('common.none')}
            isDisabled={!canEdit}
            options={sampleTypeOptions}
            {...errorFor('sampleType')}
          />
          <SelectField
            label={t('catalogue.expectedDurationUnit')}
            value={durationUnit}
            onChange={setDurationUnit}
            placeholder={t('common.none')}
            isDisabled={!canEdit}
            options={ALL_DURATION_UNITS.map((value) => ({
              value,
              label: t(`durationUnit.${value}`),
            }))}
          />
          <NumberField
            label={t('catalogue.expectedDurationValue')}
            value={durationValue}
            onChange={setDurationValue}
            isReadOnly={!canEdit}
          />
        </FormSection>

        <FormSection title={t('catalogue.commercial')}>
          {/*
            An empty price field is UNPRICED. `NumberField` hands back null for
            an empty string rather than 0, which is the whole reason it does.
          */}
          <NumberField
            label={t('catalogue.basePrice')}
            description={t('catalogue.basePriceHint')}
            value={basePrice}
            onChange={setBasePrice}
            step="0.01"
            unit={record?.currency}
            isReadOnly={!canEdit || !canPrice}
            {...errorFor('basePriceMinor')}
          />
          <SelectField
            label={t('catalogue.pricingUnitLabel')}
            value={pricingUnit}
            onChange={setPricingUnit}
            placeholder={t('common.none')}
            isDisabled={!canEdit}
            options={ALL_PRICING_UNITS.map((value) => ({
              value,
              label: t(`pricingUnit.${value}`),
            }))}
          />
          {record ? (
            <ReadOnlyField label={t('catalogue.readiness')}>
              <StatusBadge
                label={t(
                  `status.templateReadiness.${record.templateReadiness}`,
                )}
                tone={
                  record.templateReadiness === 'ready' ? 'success' : 'warning'
                }
              />
            </ReadOnlyField>
          ) : null}
          {record ? (
            <ReadOnlyField label={t('catalogue.basePrice')}>
              {/* Not `?? 0`. An unpriced service reads as unpriced. */}
              {record.basePriceMinor === null
                ? t('catalogue.notPriced')
                : formatMoney(record.basePriceMinor, {
                    locale,
                    currency: record.currency,
                  })}
            </ReadOnlyField>
          ) : null}
        </FormSection>

        <FormSection title={t('catalogue.notes')} columns={1}>
          <TextAreaField
            label={t('catalogue.notes')}
            value={notes}
            onChange={setNotes}
            rows={3}
            isReadOnly={!canEdit}
          />
        </FormSection>
    </FormPage>
    {record && canEdit ? (
      <div className="mt-gutter flex w-full max-w-4xl flex-col gap-gutter">
        <SpecialisedTemplatePanel item={record} />
        <ReportTemplatePanel item={record} />
      </div>
    ) : null}
    </>
  );
}
